import { useEffect, useState } from 'react'
import { PREFECTURES } from '../constants/prefectures'
import { useI18n } from '../i18n/useI18n'
import { api } from '../services/api'
import type { Residence } from '../lib/residence'

/**
 * #2004 居住地 (都道府県 → 市区町村) の入力。**任意・既定は未選択。**
 *
 * - 既定値を入れない: 入れると「本当にそこに住んでいる人」と「触らなかった人」の区別が付かない。
 *   現在地からの自動入力もしない (イベント会場で押したら全員その町になる)
 * - 値は**サーバのマスタにあるものだけ** (自由入力にしない)。市区町村は、都道府県を選んだ時に
 *   その県の分だけ受け取る — マスタの正はサーバの 1 か所 (services/pay/api/services/municipalities.py)
 * - 県を選んだだけでは「入力した」ことにしない。県と市が揃って初めて値になる (サーバも 2 つ一組で
 *   しか受け付けない)
 *
 * 出し分け (国籍に日本を選んだ人にだけ見せる) は呼び出し側の仕事。
 */
export default function ResidenceSelect({ value, onChange, disabled = false, selectClassName }: {
  value: Residence
  onChange: (next: Residence) => void
  disabled?: boolean
  selectClassName: string
}) {
  const { t } = useI18n()
  // 読み込んだ結果は「どの県の分か」と一緒に持つ。県が変わった瞬間に前の県の市が選択肢に残らない
  // ように、表示は「今選んでいる県の結果だけ」を使う (effect の中で同期的に state を消さない)。
  const [loaded, setLoaded] = useState<{ prefecture: string; cities: string[]; failed: boolean }>(
    { prefecture: '', cities: [], failed: false },
  )

  useEffect(() => {
    const prefecture = value.prefecture
    if (!prefecture) return
    let cancelled = false
    api.getMunicipalities(prefecture)
      .then((r) => { if (!cancelled) setLoaded({ prefecture, cities: r.cities, failed: false }) })
      .catch((e) => {
        if (cancelled) return
        console.error('[ResidenceSelect] municipalities failed', e)
        setLoaded({ prefecture, cities: [], failed: true })
      })
    return () => { cancelled = true }
  }, [value.prefecture])

  const current = loaded.prefecture === value.prefecture ? loaded : null
  const cities = current?.cities ?? []
  const failed = !!value.prefecture && !!current?.failed

  return (
    <div>
      <div className="flex gap-2">
        <label className="min-w-0 flex-1">
          <span className="font-body text-xs text-text-tertiary">{t('residence.prefecture')}</span>
          <select
            value={value.prefecture}
            disabled={disabled}
            // 県を変えたら市は必ず空に戻す (前の県の市が残ると、県と市が食い違う)
            onChange={(e) => onChange({ prefecture: e.target.value, city: '' })}
            className={selectClassName}
          >
            <option value="">{t('residence.unselected')}</option>
            {PREFECTURES.map((p) => (
              <option key={p.code} value={p.name}>{p.name}</option>
            ))}
          </select>
        </label>
        <label className="min-w-0 flex-1">
          <span className="font-body text-xs text-text-tertiary">{t('residence.city')}</span>
          <select
            value={value.city}
            disabled={disabled || !value.prefecture || (cities.length === 0 && !value.city)}
            onChange={(e) => onChange({ prefecture: value.prefecture, city: e.target.value })}
            className={selectClassName}
          >
            <option value="">{t('residence.unselected')}</option>
            {/* 一覧を読み込んでいる間・読み込めなかった時も、保存済みの市はそのまま見せる。
              * 選択肢に無いと select が「選択しない」に見えて、入れた居住地が消えたように見える */}
            {value.city && !cities.includes(value.city) && (
              <option value={value.city}>{value.city}</option>
            )}
            {cities.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
      </div>
      {failed && (
        <p className="mt-1.5 font-body text-xs text-yokoso-red">{t('residence.loadFailed')}</p>
      )}
    </div>
  )
}
